import * as logger from 'commercial-web-client-utilities/dist/logging/logger';
import remoteModuleImportMap from '@/config/remote-module-imports';

import type { App, Plugin } from 'vue';

let app: App | null = null;
let pluginQueue: Plugin[] = [];
const alreadyUsedPluginPackages: Set<string> = new Set<string>();

export function registerApp(newApp: App | null) {
    app = newApp;
    if (app) {
        for (const plugin of pluginQueue) {
            app.use(plugin);
        }
        pluginQueue = [];
    }
}

export function useApp(): App {
    return app as App;
}

export function usePlugin(plugin: Plugin) {
    if (app) {
        app.use(plugin);
    } else {
        pluginQueue.push(plugin);
    }
}

/**
 * Looks at the exports.manifest.json from each federated remote to see if it needs to
 * import and use its Vue plugin in the app.
 */
/* istanbul ignore next */
export async function usePluginsThatProvideAemComponents(componentTypes: Set<string>): Promise<void> {
    for (const packageName in remoteModuleImportMap) {
        if (!alreadyUsedPluginPackages.has(packageName)) {
            try {
                if (!remoteModuleImportMap[packageName]) {
                    throw new Error(`Package ${packageName} is not defined in remote-module-imports.ts`);
                }
                const { manifest, getVuePlugin } = await remoteModuleImportMap[packageName]();
                if (manifest && getVuePlugin) {
                    const providedAemComponents: Array<string> = manifest?.provides?.aemComponents ?? [];
                    let isUsePlugin = false;
                    for (const providedComponentType of providedAemComponents) {
                        if (componentTypes.has(providedComponentType)) {
                            isUsePlugin = true;
                            break;
                        }
                    }
                    if (isUsePlugin) {
                        alreadyUsedPluginPackages.add(packageName);
                        usePlugin(await getVuePlugin());
                    }
                }
            } catch (error) {
                if ((error as Error)?.toString().includes('ScriptExternalLoadError')) {
                    logger.error(
                        `The module ${packageName} appears to not be running. Components in this package won't load.`
                    );
                } else {
                    logger.error(
                        'An error occurred when attempting to import and use a plugin from a federated module.',
                        error
                    );
                }
                // Don't keep attempting to load the package, clutter the console with errors.
                alreadyUsedPluginPackages.add(packageName);
            }
        }
    }
}